/* ============================================================
   BANDSINTOWN LAYER (bandsintown.js)
   ------------------------------------------------------------
   Pulls Obed's live tour dates from the Bandsintown public API
   and renders them into the "On The Road" card slide in the
   Tour tab (feed.js → buildTourDatesSlide). The card stays
   inside the feed experience — no widget iframe, no drift from
   the site's look.

   Data is fetched fresh on every page load, so posting a show
   to Bandsintown (already part of the KEM workflow) updates the
   site automatically — nothing to sync.

   ⚠ app_id: 'squarespace' is a PLACEHOLDER that works today
   because Bandsintown allows that partner id publicly. For
   production, register a proper app_id / API access with
   Bandsintown for Artists and drop it into CONFIG.bandsintown
   in data.js. Endpoint shapes are identical.
   ============================================================ */

const Bandsintown = (() => {
  const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

  function fmtDate(iso) {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
  }

  function base() {
    return `https://rest.bandsintown.com/artists/id_${CONFIG.bandsintown.artistId}`;
  }
  function appId() { return `app_id=${CONFIG.bandsintown.appId}`; }

  /** Fetch artist + upcoming events, then render into `body`.
      8s abort guard so a dead network shows the fallback instead
      of an eternal "Checking the road…". */
  async function load(body) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 8000);
    try {
      const [artistRes, eventsRes] = await Promise.all([
        fetch(`${base()}?${appId()}`, { signal: ctrl.signal }),
        fetch(`${base()}/events?${appId()}`, { signal: ctrl.signal }),   // default = upcoming only
      ]);
      clearTimeout(timer);
      const artist = await artistRes.json();
      const events = await eventsRes.json();
      render(body, artist, Array.isArray(events) ? events : []);
    } catch (_) {
      body.innerHTML = `
        <div class="bit-empty">
          <p>Couldn’t reach the tour feed right now.</p>
          <a class="btn btn--ghost bit-btn" target="_blank" rel="noopener"
             href="https://www.bandsintown.com/a/${CONFIG.bandsintown.artistId}">
             See dates on Bandsintown</a>
        </div>`;
    }
  }

  function render(body, artist, events) {
    const trackers = artist?.tracker_count
      ? `<div class="bit-trackers">${Interactions.formatCount(artist.tracker_count)} fans tracking</div>`
      : '';

    if (!events.length) {
      /* No announced dates — turn the moment into signups. */
      body.innerHTML = `
        ${trackers}
        <div class="bit-empty">
          <div class="bit-empty__mark">🗺</div>
          <p class="bit-empty__lead">No dates announced — yet.</p>
          <p class="bit-empty__sub">Backstage members hear about shows and
            presales before anyone else.</p>
          <button class="btn btn--gold bit-btn" data-bit-backstage>Get the Backstage Pass</button>
          <a class="btn btn--ghost bit-btn" target="_blank" rel="noopener"
             href="https://www.bandsintown.com/a/${CONFIG.bandsintown.artistId}">
             Track on Bandsintown</a>
        </div>`;
      body.querySelector('[data-bit-backstage]')
        ?.addEventListener('click', () => Interactions.openFanForm('backstage'));
      return;
    }

    body.innerHTML = `
      ${trackers}
      <div class="ticket__shows bit-shows">
        ${events.map(ev => {
          const v = ev.venue || {};
          const where = [v.city, v.region || v.country].filter(Boolean).join(', ');
          const link = ev.offers?.[0]?.url || ev.url;
          const label = ev.offers?.length ? 'Tickets' : 'RSVP';
          return `
            <a class="ticket__show bit-show" href="${link}" target="_blank" rel="noopener">
              <span class="ticket__date">${fmtDate(ev.datetime)}</span>
              <span class="ticket__city">${where}</span>
              <span class="ticket__venue">${v.name || ''} · ${label} →</span>
            </a>`;
        }).join('')}
      </div>
      <a class="btn btn--ghost bit-btn" target="_blank" rel="noopener"
         href="https://www.bandsintown.com/a/${CONFIG.bandsintown.artistId}">
         All dates on Bandsintown</a>`;
  }

  return { load };
})();
